import React, { useState, useEffect, useCallback } from 'react';
import { adminSettings } from '../../utils/api';
import toast from 'react-hot-toast';

const prettyKey = (k) => (k || '')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, c => c.toUpperCase());

const groupOrder = ['general', 'contact', 'shipping', 'payment', 'social', 'seo', 'misc'];
const sortGroups = (a, b) => {
  const ai = groupOrder.indexOf(a), bi = groupOrder.indexOf(b);
  if (ai === -1 && bi === -1) return a.localeCompare(b);
  if (ai === -1) return 1;
  if (bi === -1) return -1;
  return ai - bi;
};

const AdminSettings = () => {
  const [settings, setSettings] = useState([]);
  const [draft, setDraft] = useState({}); // key -> new value
  const [savingKey, setSavingKey] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    adminSettings.getAll()
      .then(rows => {
        setSettings(rows);
        setDraft({}); // reset drafts after reload
      })
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleChange = (key, value) => {
    setDraft(d => ({ ...d, [key]: value }));
  };

  const handleSave = async (s) => {
    const value = draft[s.setting_key];
    if (value === undefined || value === s.setting_value) return;
    setSavingKey(s.setting_key);
    try {
      await adminSettings.update(s.setting_key, value);
      toast.success(`${prettyKey(s.setting_key)} saved`);
      // optimistic update
      setSettings(rows => rows.map(r =>
        r.setting_key === s.setting_key ? { ...r, setting_value: value } : r
      ));
      setDraft(d => { const c = { ...d }; delete c[s.setting_key]; return c; });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingKey(null);
    }
  };

  // group by group_name
  const grouped = settings.reduce((acc, s) => {
    const g = s.group_name || 'misc';
    (acc[g] ||= []).push(s);
    return acc;
  }, {});

  const groupNames = Object.keys(grouped).sort(sortGroups);

  const renderField = (s) => {
    const value = draft[s.setting_key] !== undefined ? draft[s.setting_key] : (s.setting_value ?? '');
    const dirty = draft[s.setting_key] !== undefined && draft[s.setting_key] !== s.setting_value;

    const commonProps = {
      value,
      onChange: e => handleChange(s.setting_key, e.target.value),
      className: 'form-control',
    };

    let input;
    if (s.setting_type === 'boolean') {
      input = (
        <select {...commonProps}>
          <option value="1">Yes</option>
          <option value="0">No</option>
        </select>
      );
    } else if (s.setting_type === 'number') {
      input = <input type="number" step="any" {...commonProps} />;
    } else if (s.setting_type === 'longtext' || (s.setting_value && s.setting_value.length > 80)) {
      input = <textarea rows="3" {...commonProps} />;
    } else {
      input = <input type="text" {...commonProps} />;
    }

    return (
      <div key={s.setting_key} className="setting-row">
        <div className="setting-row__label">
          <strong>{prettyKey(s.setting_key)}</strong>
          <small>{s.setting_key}</small>
        </div>
        <div className="setting-row__input">{input}</div>
        <div className="setting-row__actions">
          <button
            className="btn btn-primary"
            disabled={!dirty || savingKey === s.setting_key}
            onClick={() => handleSave(s)}
          >
            {savingKey === s.setting_key ? 'Saving…' : (dirty ? 'Save' : 'Saved')}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="admin-page-header">
        <h2 style={{ fontFamily:'var(--font-serif)', fontSize:'1.5rem' }}>Settings</h2>
        <p style={{ color:'var(--text-muted)', marginTop:4, fontSize:'0.88rem' }}>
          Site-wide configuration. Changes save per row.
        </p>
      </div>

      {loading ? (
        <div className="admin-card"><div style={{ textAlign:'center', padding:40 }}><div className="spinner" style={{ margin:'0 auto' }} /></div></div>
      ) : settings.length === 0 ? (
        <div className="admin-card" style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>
          No settings configured.
        </div>
      ) : (
        groupNames.map(g => (
          <div key={g} className="admin-card" style={{ marginBottom: 20 }}>
            <h3 className="settings-group-title">{prettyKey(g)}</h3>
            <div className="settings-list">
              {grouped[g].map(renderField)}
            </div>
          </div>
        ))
      )}

      <style>{`
        .admin-card { background: white; border-radius: var(--radius-lg); padding: 24px; box-shadow: var(--shadow-sm); }
        .settings-group-title { font-family: var(--font-serif); font-size: 1.1rem; margin-bottom: 16px; padding-bottom: 10px; border-bottom: 1px solid var(--border); text-transform: capitalize; }
        .settings-list { display: flex; flex-direction: column; gap: 12px; }
        .setting-row { display: grid; grid-template-columns: 220px 1fr auto; gap: 16px; align-items: center; padding: 10px 0; border-bottom: 1px solid #f5f5f5; }
        .setting-row:last-child { border-bottom: none; }
        .setting-row__label strong { display: block; font-size: 0.9rem; }
        .setting-row__label small { color: var(--text-muted); font-size: 0.72rem; font-family: monospace; }
        .setting-row__actions .btn-primary { padding: 7px 16px; font-size: 0.82rem; }
        .setting-row__actions .btn-primary:disabled { opacity: 0.5; }
        @media (max-width: 720px) {
          .setting-row { grid-template-columns: 1fr; gap: 8px; }
          .setting-row__actions { justify-self: start; }
        }
      `}</style>
    </div>
  );
};

export default AdminSettings;
